/*
 * File: GetStudentCourseEnrollments.ts
 * Author: Austin Robinson
 * Date Created: 8/15/2023
 * Copyright (c) DrivingDoc 2023
 */

import { PostGreSQLInterval, privateApi } from 'api';
import { createQuery } from 'react-query-kit'

export type GetStudentCourseEnrollmentsParams = {
    id: string;
};

export type StudentCourseEnrollment = {
    tenant_id: string,
    id: string,
    student_user_id: string,
    course_section_id: string,
    student_charge_id: string | null,
    package_course_package_id: string | null,
    package_course_course_id: string | null,
    course_policy_course_id: string,
    course_policy_updated_at: string,
    course_enrollment_criteria_course_id: string,
    course_enrollment_criteria_updated_at: string,
    course_self_schedule_criteria_course_id: string | null,
    course_self_schedule_criteria_updated_at: string | null,
    course_completion_criteria_course_id: string,
    course_completion_criteria_updated_at: string,
    secure_seat_mode: 'at_time_of_enrollment' | 'when_deposit_paid' | 'when_full_cost_paid',
    seat_secured_at: boolean,
    enrolled_at: string,
    created_by: string | null,
    completed_at: string | null,
    tenant_location_id: string | null,
    performance_time_included: PostGreSQLInterval | null,
    observation_time_included: PostGreSQLInterval | null,
    requisite_group: number,
    became_eligible_at: string | null,
    must_complete_by: string | null,
    course_section: {
        id: string,
        course_id: string,
        title: string,
        description: string | null
    },
    course: {
        id: string,
        title: string | null,
        description: string | null,
        course_type: 'behind_the_wheel' | 'in_person' | 'synchronous_online' | 'fully_online',
        schedule_type: 'self_scheduled' | 'fixed' | 'manual' | 'no_schedule',
    },
    course_policy: {
        tenant_id: string,
        course_id: string,
        updated_at: string,
        min_time_to_complete: PostGreSQLInterval | null,
        max_time_to_complete: PostGreSQLInterval | null,
        allow_self_service_extensions: boolean | null,
        standard_extension_time: PostGreSQLInterval | null,
        standard_extension_fee: number | null,
        max_extensions: number | null,
        max_time_to_request_extension: PostGreSQLInterval | null,
        absences_allowed_before_fee: number | null,
        absence_fee: number | null,
        absence_fee_due: PostGreSQLInterval | null,
        absence_fee_due_lock: PostGreSQLInterval | null,
        no_permits_allowed_before_fee: number | null,
        no_permit_fee: number | null,
        no_permit_fee_due: PostGreSQLInterval | null,
        no_permit_fee_due_lock: PostGreSQLInterval | null,
        allow_cancellations: boolean | null,
        cancellations_allowed_before_fee: number | null,
        cancellation_fee: number | null,
        cancellation_fee_due: PostGreSQLInterval | null,
        cancellation_fee_due_lock: PostGreSQLInterval | null,
        late_cancellations_allowed_before_fee: number | null,
        late_cancellation_fee: number | null,
        late_cancellation_fee_due: PostGreSQLInterval | null,
        late_cancellation_fee_due_lock: PostGreSQLInterval | null,
        late_cancellation_deadline: PostGreSQLInterval | null,
    },
    performance_time_completed: PostGreSQLInterval | null,
    performance_time_scheduled: PostGreSQLInterval | null,
    observation_time_completed: PostGreSQLInterval | null,
    observation_time_scheduled: PostGreSQLInterval | null,
    course_completion_criteria: {
        performance_time_required: PostGreSQLInterval | null,
        observation_time_required: PostGreSQLInterval | null
    }
};

async function getStudentCourseEnrollments(params: GetStudentCourseEnrollmentsParams) {
    if (!params.id) {
        return null
    }
    // make api call
    const result = await privateApi.get(`${import.meta.env.VITE_API_BASE_URL}/users/students/${params.id}/courseEnrollments`, {

    });

    return result.data as StudentCourseEnrollment[];
}

const useGetStudentCourseEnrollments = createQuery<StudentCourseEnrollment[] | null, GetStudentCourseEnrollmentsParams, Error>({
    queryKey: ['student_course_enrollments'],
    fetcher: (variables) => getStudentCourseEnrollments(variables),
});

export { useGetStudentCourseEnrollments };


